/**
 * Map tl-app's CleverTap-style calls onto the Wisetap ingest schema
 * (wisetap.oa_user_events / oa_user_profiles).
 *
 * The worker (wisetap-worker-oa-ingester) structs type is_test /
 * is_whatsapp_opted as strings, so those scalars are sent as "true"/"false".
 * `properties` is a nested object (the worker/CH handle it).
 */
import type {
  ContextSnapshot,
  EventProps,
  OaUserEvent,
  OaUserProfile,
  Traits,
} from "./types";

const asStr = (v: unknown): string | null => (v == null ? null : String(v));

/** Like asStr, but "" / whitespace is absence rather than an id. */
const asId = (v: unknown): string | null => {
  const s = asStr(v)?.trim();
  return s || null;
};

const stringifyProps = (props?: EventProps): Record<string, string> => {
  if (!props || typeof props !== "object") return {};
  return Object.keys(props).reduce<Record<string, string>>((acc, k) => {
    const v = props[k];
    if (v === null || v === undefined) return acc;
    acc[k] = typeof v === "object" ? JSON.stringify(v) : String(v);
    return acc;
  }, {});
};

/** One oa_user_events row. triggered_at stamped here (enqueue time). */
export const toEvent = (
  eventName: string,
  props: EventProps | undefined,
  ctx: ContextSnapshot,
  sessionId: string,
): OaUserEvent => {
  const src = props?.source ?? props?.screen_name;
  return {
    app_name: ctx.app_name,
    external_user_id: ctx.external_user_id,
    unique_device_id: ctx.unique_device_id,
    phone_number: ctx.phone_number,
    event_name: eventName,
    source: src != null ? String(src) : null,
    lat: ctx.lat != null ? String(ctx.lat) : null,
    lng: ctx.lng != null ? String(ctx.lng) : null,
    triggered_at: new Date().toISOString(),
    properties: {
      ...stringifyProps(props),
      // Deliberately NOT `user_id` — call sites already use that key for the
      // customer an event is about (RRD cards, shop updates). Last-wins would
      // clobber them and make one key mean two things.
      ...(ctx.user_id ? { leader_user_id: ctx.user_id } : {}),
    },
    platform: ctx.platform,
    app_version: ctx.app_version,
    os_version: ctx.os_version,
    session_id: sessionId,
    device_model: ctx.device_model,
    is_test: String(ctx.is_test),
  };
};

/** Stamp ingested_at across a batch at flush time. */
export const stampIngestedAt = (rows: OaUserEvent[]): OaUserEvent[] => {
  const ingested_at = new Date().toISOString();
  return rows.map((r) => ({ ...r, ingested_at }));
};

const PROFILE_KNOWN_KEYS = new Set<string>([
  "name",
  "user_name",
  "email",
  "gender",
  "language",
  "city",
  "state",
  "pincode",
  "is_whatsapp_opted",
  "phone_number",
  "created_at",
  "createdAt",
  "updated_at",
]);

/**
 * One oa_user_profiles object. Every schema column is present on every call
 * (null when unknown). Unmapped scalar traits fall into `properties`.
 * created_at is taken from the user object when available; if absent we send
 * `now` (ReplacingMergeTree dedups by updated_at, so a real created_at from the
 * source is preferred to keep it stable).
 */
export const toProfile = (
  userId: string | number,
  traits: Traits | undefined,
  ctx: ContextSnapshot,
): OaUserProfile => {
  const t = traits || {};
  const now = new Date().toISOString();
  const leaderUserId = asId(t.user_id) ?? ctx.user_id;
  const extra = Object.keys(t).reduce<Record<string, string>>((acc, k) => {
    const v = t[k];
    if (!PROFILE_KNOWN_KEYS.has(k) && v != null && typeof v !== "object") {
      acc[k] = String(v);
    }
    return acc;
  }, {});
  return {
    app_name: ctx.app_name,
    external_user_id: asId(userId) ?? ctx.external_user_id,
    phone_number: ctx.phone_number,
    name: asStr(t.name) || asStr(t.user_name),
    email: asStr(t.email),
    gender: asStr(t.gender),
    language: asStr(t.language),
    city: asStr(t.city),
    state: asStr(t.state),
    pincode: asStr(t.pincode),
    lat: ctx.lat != null ? String(ctx.lat) : null,
    lng: ctx.lng != null ? String(ctx.lng) : null,
    platform: ctx.platform,
    app_version: ctx.app_version,
    device_model: ctx.device_model,
    is_whatsapp_opted: String(!!t.is_whatsapp_opted),
    // Same key as the event rows so `leader_user_id` means the leader on both
    // tables. Traits win: identify() is handed the /leader/me object directly,
    // which is authoritative even if the store dispatch hasn't landed yet.
    properties: {
      ...extra,
      ...(leaderUserId ? { leader_user_id: leaderUserId } : {}),
    },
    updated_at: asStr(t.updated_at) || now,
    created_at: asStr(t.created_at) || asStr(t.createdAt) || now,
  };
};
